import { downloadPublicMedia } from '../../lib/mediaUrls';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import SyncedLyrics from '../SyncedLyrics';
import { MusicPlayerProps, Song, Album, RepeatMode, ShuffleMode, STORAGE_KEYS } from './types';
import { loadFromLocalStorage, saveToLocalStorage, formatTime, generateShuffledQueue, getNextSong, getPreviousSong } from './playerUtils';
import { setupHlsPlayback, setupNativeHlsPlayback, setupChromeOperaSpecialPlayback } from './HLSPlayerUtils';
import { fetchAlbums, getSongStreamUrl, checkUrlAccessible, revokeAllCoverBlobUrls } from './musicPlayerApi';
import { getProperImageUrl } from './utils';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import ScrollingText from './ScrollingText';
import RepeatButton from './RepeatButton';
import MobileSongInfo from './MobileSongInfo';
import { DesktopControls, MobileControls, ProgressBar, BottomControls } from './PlayerControls';
import { AlbumDisplay, PlaylistButton, Playlist, AlbumList } from './PlaylistComponents';
import AudioErrorDisplay from './AudioErrorDisplay';
import VolumeControl from './VolumeControl';
import { Shuffle, Volume2, VolumeX, Repeat, Repeat1, ListMusic, Download, ChevronDown, ChevronUp, Music } from 'lucide-react';
import './MusicPlayer.css';
import toast from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';

// Add strict HLS-only flag at the top of the file
const USE_ONLY_HLS = true; // Force HLS streaming for all audio
// Get API base URL from environment - always use relative path
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const MusicPlayer: React.FC<MusicPlayerProps> = ({ onVisualizerUpdate }) => {
  // Get initial values from localStorage
  const initialVolume = loadFromLocalStorage(STORAGE_KEYS.VOLUME, 1);
  const initialMuted = loadFromLocalStorage(STORAGE_KEYS.IS_MUTED, false);
  const initialRepeatMode = localStorage.getItem(STORAGE_KEYS.REPEAT_MODE) as RepeatMode || 'none';
  const initialShuffleMode = loadFromLocalStorage(STORAGE_KEYS.SHUFFLE_MODE, 'none') as ShuffleMode;
  const initialCurrentTime = loadFromLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [showAlbums, setShowAlbums] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [volume, setVolume] = useState(initialVolume);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialCurrentTime);
  const [songUrl, setSongUrl] = useState<string | null>(null);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);

  // Volume control state
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const volumeHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs for menus and audio element
  const albumMenuRef = useRef<HTMLDivElement>(null);
  const playlistMenuRef = useRef<HTMLDivElement>(null);
  const albumButtonRef = useRef<HTMLButtonElement>(null);
  const playlistButtonRef = useRef<HTMLButtonElement>(null);
  const desktopProgressRef = useRef<HTMLDivElement>(null);
  const mobileProgressRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Playback modes
  const [repeatMode, setRepeatMode] = useState<RepeatMode>(initialRepeatMode);
  const [shuffleMode, setShuffleMode] = useState<ShuffleMode>(initialShuffleMode);
  const [shuffledQueue, setShuffledQueue] = useState<Song[]>([]);
  
  // Visualizer state
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [source, setSource] = useState<MediaElementAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  // Add a ref to track visualization initialization
  const visualizerInitializedRef = useRef<boolean>(false);

  // Album and song info
  const [currentAlbum, setCurrentAlbum] = useState<Album | null>(null);
  const [coverArt, setCoverArt] = useState<string>('');
  const [currentSongId, setCurrentSongId] = useState<string | null>(null);

  // Error handling state
  const [audioError, setAudioError] = useState<string | null>(null);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [detailedError, setDetailedError] = useState<string | null>(null);
  const [browserInfo, setBrowserInfo] = useState<string | null>(null);

  // HLS instance state
  const [hlsInstance, setHlsInstance] = useState<Hls | null>(null);

  // Mobile portal state
  const [mobileInfoPortalElement, setMobileInfoPortalElement] = useState<HTMLElement | null>(null);


  // Playlist scroll indicators state
  const [showPlaylistTopArrow, setShowPlaylistTopArrow] = useState(false);
  const [showPlaylistBottomArrow, setShowPlaylistBottomArrow] = useState(false);
  const playlistScrollRef = useRef<HTMLDivElement>(null);

  // Refs
  const draggingRef = useRef<React.RefObject<HTMLDivElement> | null>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);

  // Load albums when component mounts; revoke cover blob URLs on unmount to
  // avoid leaking object URLs.
  useEffect(() => {
    loadAlbums();
    return () => {
      revokeAllCoverBlobUrls();
    };
  }, []);

  // Log current song details when it changes
  useEffect(() => {
    if (currentSong) {
      // console.log("Current song in MusicPlayer:", currentSong);
      // console.log("Downloadable flag:", currentSong.downloadable);
    }
  }, [currentSong]);

  // Load portal elements
  useEffect(() => {
    const infoPortalElement = document.getElementById('mobile-song-info');
    if (infoPortalElement) {
      setMobileInfoPortalElement(infoPortalElement);
    }
  }, []);

  // Detect browser info for troubleshooting
  useEffect(() => {
    // Log browser information for debugging
    const userAgent = navigator.userAgent;
    
    // Detect browser type
    const isChrome = /chrome/i.test(userAgent) && !/edge|edg/i.test(userAgent);
    const isOpera = /OPR|Opera/i.test(userAgent);
    const isEdge = /edge|edg/i.test(userAgent);
    const isFirefox = /firefox/i.test(userAgent);
    const isSafari = /safari/i.test(userAgent) && !/chrome|chromium/i.test(userAgent);
    
    // Save browser information for later use
    setBrowserInfo(`
      Browser: ${
        isChrome ? 'Chrome' : isOpera ? 'Opera' : isEdge ? 'Edge' : 
        isFirefox ? 'Firefox' : isSafari ? 'Safari' : 'Other'
      }
      User Agent: ${userAgent}
      Platform: ${navigator.platform}
    `);
  }, []);

  // Apply cover art when current song changes
  useEffect(() => {
    if (currentSong) {
      // Find album for current song
      const album = albums.find(a => a.songs.some((s: Song) => s.id === currentSong.id));
      setCurrentAlbum(album || null);
      
      // Set cover art based on song or album
      const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
      const staticBaseUrl = apiBaseUrl.replace('/api', ''); // Remove /api for static assets
      
      if (currentSong.coverUrl) {
        // Check if it's a blob URL - use it directly without prepending baseUrl
        if (currentSong.coverUrl.startsWith('blob:')) {
          setCoverArt(currentSong.coverUrl);
        } else {
          setCoverArt(`${staticBaseUrl}${currentSong.coverUrl}`);
        }
      } else if (album?.coverUrl) {
        // Check if it's a blob URL - use it directly without prepending baseUrl
        if (album.coverUrl.startsWith('blob:')) {
          setCoverArt(album.coverUrl);
        } else {
          setCoverArt(`${staticBaseUrl}${album.coverUrl}`);
        }
      } else {
        setCoverArt(''); // Default/placeholder
      }
    }
  }, [currentSong, albums]);

  // Handle play/pause
  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    // If we're going to play
    if (!isPlaying) {
      // Initialize visualizer on first play if not already done
      if (!visualizerInitializedRef.current && audioRef.current) {
        try {
          // Create AudioContext if needed
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass && !audioContext) {
            const newAudioContext = new AudioContextClass();
            const newAnalyser = newAudioContext.createAnalyser();
            newAnalyser.fftSize = 2048;
            newAnalyser.smoothingTimeConstant = 0.8;
            
            const newSource = newAudioContext.createMediaElementSource(audioRef.current);
            newSource.connect(newAnalyser);
            newAnalyser.connect(newAudioContext.destination);
            
            // Update state
            setAudioContext(newAudioContext);
            setSource(newSource);
            setAnalyser(newAnalyser);
            
            // Mark as initialized
            visualizerInitializedRef.current = true;
            // console.log('Audio visualization initialized by play button');
            
            // Resume the context to ensure it's active
            newAudioContext.resume().then(() => {
              if (onVisualizerUpdate) {
                onVisualizerUpdate(newAnalyser, true);
              }
            });
          } 
          // If context exists but no visualization is active
          else if (audioContext && !visualizerInitializedRef.current) {
            // Resume existing context
            audioContext.resume().then(() => {
              // console.log('Resumed existing AudioContext');
              if (analyser && onVisualizerUpdate) {
                onVisualizerUpdate(analyser, true);
                visualizerInitializedRef.current = true;
              }
            });
          }
        } catch (error) {
          console.error('Failed to initialize visualizer on play:', error);
        }
      } 
      // If visualizer is already initialized but context may be suspended
      else if (audioContext && analyser && onVisualizerUpdate) {
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            onVisualizerUpdate(analyser, true);
          });
        }
      }
      
      // Play the audio
      audioRef.current.play()
        .catch(error => console.error("Play error:", error));
    } else {
      // Otherwise pause
      audioRef.current.pause();
    }
  };

  // Handle song end - play next song or repeat current
  const handleSongEnd = () => {
    // Get the current repeat mode from state rather than localStorage
    // This ensures we're using the current value, not a potentially stale one
    
    if (repeatMode === 'song' && audioRef.current) {
      // For single song repeat, restart the current song
      // console.log('Song ended, repeat mode is "song", restarting song');
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.error('Error restarting song:', err);
      });
    } else {
      // Reset current time before playing next song
      // console.log(`Song ended, repeat mode is "${repeatMode}", playing next song`);
      setCurrentTime(0);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      
      // Play next song according to playlist/shuffle settings
      playNextSong();
    }
  };

  // Play next song based on queue
  const playNextSong = useCallback(() => {
    if (!currentSong) return;
    
    try {
      // console.log("Finding next song...");
      
      // Force repeat mode to none for this specific operation if it's currently on song
      // This allows us to get the actual next song even when repeat is on
      const effectiveRepeatMode = repeatMode === 'song' ? 'none' : repeatMode;
      
      // console.log(`Using effective repeat mode: ${effectiveRepeatMode} (actual: ${repeatMode})`);
      
      const nextSong = getNextSong(
        currentSong,
        effectiveRepeatMode, // Use the effective mode here
        shuffleMode,
        shuffledQueue,
        currentAlbum
      );
      
      if (nextSong && nextSong.id !== currentSong.id) {
        // console.log(`Found next song: ${nextSong.name} (${nextSong.id})`);
        
        // If the current song failed with a 404, reset some state
        if (audioError) {
          setAudioError(null);
          setDetailedError(null);
        }
        
        // Always use selectSong to play the next song
        if (nextSong) {
          // Set the new song directly here instead of using selectSong
          // to avoid the circular dependency
          // console.log(`Setting next song: ${nextSong.name}`);
          setIsLoading(true);
          getSongStreamUrl(nextSong.id).then(streamUrl => {
            if (streamUrl) {
              setCurrentSongId(nextSong.id);
              setCurrentSong({ ...nextSong, hlsUrl: streamUrl, isHls: streamUrl.includes('.m3u8') });
              setSongUrl(streamUrl);
              
              // Reset current time
              setCurrentTime(0);
              saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
              
              // Setup playback
              const isHlsStream = streamUrl.includes('.m3u8');
              if (isHlsStream) {
                setupHlsPlayback(streamUrl, audioRef, setHlsInstance, hlsInstance, true);
              } else {
                if (hlsInstance) {
                  try {
                    hlsInstance.destroy();
                  } catch (err) {
                    console.error('Error destroying HLS instance:', err);
                  }
                  setHlsInstance(null);
                }
                
                if (audioRef.current) {
                  audioRef.current.src = streamUrl;
                  audioRef.current.currentTime = 0;
                  audioRef.current.load();
                  audioRef.current.play().catch(err => {
                    console.error('Error playing:', err);
                  });
                }
              }
            }
            setIsLoading(false);
          }).catch(err => {
            console.error('Error getting stream URL:', err);
            setIsLoading(false);
          });
        }
      } else if (repeatMode === 'album' && currentAlbum && currentAlbum.songs && currentAlbum.songs.length > 0) {
        // console.log('Reached end of album, going back to first song');
        // If we're at the end of the album and repeat is on, go to the first song
        const firstSong = currentAlbum.songs[0];
        if (firstSong && firstSong.id !== currentSong.id) {
          // Use the same direct approach instead of selectSong
          setIsLoading(true);
          getSongStreamUrl(firstSong.id).then(streamUrl => {
            if (streamUrl) {
              setCurrentSongId(firstSong.id);
              setCurrentSong({ ...firstSong, hlsUrl: streamUrl, isHls: streamUrl.includes('.m3u8') });
              setSongUrl(streamUrl);
              
              // Reset current time
              setCurrentTime(0);
              saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
              
              // Setup playback
              const isHlsStream = streamUrl.includes('.m3u8');
              if (isHlsStream) {
                setupHlsPlayback(streamUrl, audioRef, setHlsInstance, hlsInstance, true);
              } else {
                if (hlsInstance) {
                  try {
                    hlsInstance.destroy();
                  } catch (err) {
                    console.error('Error destroying HLS instance:', err);
                  }
                  setHlsInstance(null);
                }
                
                if (audioRef.current) {
                  audioRef.current.src = streamUrl;
                  audioRef.current.currentTime = 0;
                  audioRef.current.load();
                  audioRef.current.play().catch(err => {
                    console.error('Error playing:', err);
                  });
                }
              }
            }
            setIsLoading(false);
          }).catch(err => {
            console.error('Error getting stream URL:', err);
            setIsLoading(false);
          });
        }
      } else {
        // console.log('No next song available or already at the last song');
        // If no next song and no repeat, just restart current song
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => {
            console.error('Error restarting current song:', err);
          });
        }
      }
    } catch (error) {
      console.error("Error in playNextSong:", error);
      toast.error("Error playing next song");
    }
  }, [currentSong, repeatMode, shuffleMode, shuffledQueue, currentAlbum, audioError, hlsInstance]);

  // Play previous song based on queue
  const playPreviousSong = useCallback(() => {
    if (!currentSong) return;
    
    try {
      // console.log("Finding previous song...");
      
      // Force repeat mode to none for this specific operation if it's currently on song
      // This allows us to get the actual previous song even when repeat is on
      const effectiveRepeatMode = repeatMode === 'song' ? 'none' : repeatMode;
      
      // console.log(`Using effective repeat mode: ${effectiveRepeatMode} (actual: ${repeatMode})`);
      
      const prevSong = getPreviousSong(
        currentSong,
        effectiveRepeatMode, // Use the effective mode here
        shuffleMode,
        shuffledQueue,
        currentAlbum
      );
      
      if (prevSong && prevSong.id !== currentSong.id) {
        // console.log(`Found previous song: ${prevSong.name} (${prevSong.id})`);
        
        // If the current song failed with a 404, reset some state
        if (audioError) {
          setAudioError(null);
          setDetailedError(null);
        }
        
        // Set the new song directly here instead of using selectSong
        // to avoid the circular dependency
        if (prevSong) {
          // console.log(`Setting previous song: ${prevSong.name}`);
          setIsLoading(true);
          getSongStreamUrl(prevSong.id).then(streamUrl => {
            if (streamUrl) {
              setCurrentSongId(prevSong.id);
              setCurrentSong({ ...prevSong, hlsUrl: streamUrl, isHls: streamUrl.includes('.m3u8') });
              setSongUrl(streamUrl);
              
              // Reset current time
              setCurrentTime(0);
              saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
              
              // Setup playback
              const isHlsStream = streamUrl.includes('.m3u8');
              if (isHlsStream) {
                setupHlsPlayback(streamUrl, audioRef, setHlsInstance, hlsInstance, true);
              } else {
                if (hlsInstance) {
                  try {
                    hlsInstance.destroy();
                  } catch (err) {
                    console.error('Error destroying HLS instance:', err);
                  }
                  setHlsInstance(null);
                }
                
                if (audioRef.current) {
                  audioRef.current.src = streamUrl;
                  audioRef.current.currentTime = 0;
                  audioRef.current.load();
                  audioRef.current.play().catch(err => {
                    console.error('Error playing:', err);
                  });
                }
              }
            }
            setIsLoading(false);
          }).catch(err => {
            console.error('Error getting stream URL:', err);
            setIsLoading(false);
          });
        }
      } else if (repeatMode === 'album' && currentAlbum && currentAlbum.songs && currentAlbum.songs.length > 0) {
        // console.log('At beginning of album, going to last song');
        // If we're at the beginning of the album and repeat is on, go to the last song
        const lastSong = currentAlbum.songs[currentAlbum.songs.length - 1];
        if (lastSong && lastSong.id !== currentSong.id) {
          // Use the same direct approach instead of selectSong
          setIsLoading(true);
          getSongStreamUrl(lastSong.id).then(streamUrl => {
            if (streamUrl) {
              setCurrentSongId(lastSong.id);
              setCurrentSong({ ...lastSong, hlsUrl: streamUrl, isHls: streamUrl.includes('.m3u8') });
              setSongUrl(streamUrl);
              
              // Reset current time
              setCurrentTime(0);
              saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
              
              // Setup playback
              const isHlsStream = streamUrl.includes('.m3u8');
              if (isHlsStream) {
                setupHlsPlayback(streamUrl, audioRef, setHlsInstance, hlsInstance, true);
              } else {
                if (hlsInstance) {
                  try {
                    hlsInstance.destroy();
                  } catch (err) {
                    console.error('Error destroying HLS instance:', err);
                  }
                  setHlsInstance(null);
                }
                
                if (audioRef.current) {
                  audioRef.current.src = streamUrl;
                  audioRef.current.currentTime = 0;
                  audioRef.current.load();
                  audioRef.current.play().catch(err => {
                    console.error('Error playing:', err);
                  });
                }
              }
            }
            setIsLoading(false);
          }).catch(err => {
            console.error('Error getting stream URL:', err);
            setIsLoading(false);
          });
        }
      } else {
        // console.log('No previous song available or already at the first song');
        // If no previous song, just restart current song
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => {
            console.error('Error restarting current song:', err);
          });
        }
      }
    } catch (error) {
      console.error("Error in playPreviousSong:", error);
      toast.error("Error playing previous song");
    }
  }, [currentSong, repeatMode, shuffleMode, shuffledQueue, currentAlbum, audioError, hlsInstance]);

  // Generate shuffled queue when shuffle mode changes
  useEffect(() => {
    if (shuffleMode !== 'none') {
      setShuffledQueue(generateShuffledQueue(
        shuffleMode,
        currentSong,
        currentAlbum,
        albums
      ));
    }
  }, [shuffleMode, currentSong, currentAlbum, albums]);

  // Toggle repeat mode
  const toggleRepeatMode = useCallback(() => {
    // Compute next mode based on current state, not localStorage
    let newMode: RepeatMode;
    if (repeatMode === 'none') newMode = 'album';
    else if (repeatMode === 'album') newMode = 'song';
    else newMode = 'none';
    
    // console.log(`Changing repeat mode from ${repeatMode} to ${newMode}`);
    
    // Update state first
    setRepeatMode(newMode);
    
    // Then save to localStorage
    localStorage.setItem(STORAGE_KEYS.REPEAT_MODE, newMode);
  }, [repeatMode]);

  // Fix initial repeat mode loading from localStorage
  useEffect(() => {
    const storedRepeatMode = localStorage.getItem(STORAGE_KEYS.REPEAT_MODE) as RepeatMode || 'none';
    // console.log(`Initial repeat mode from localStorage: ${storedRepeatMode}`);
    setRepeatMode(storedRepeatMode);
  }, []);

  // Toggle shuffle mode
  const toggleShuffleMode = () => {
    setShuffleMode((current: ShuffleMode) => {
      const newMode = current === 'none' ? 'album' : current === 'album' ? 'all' : 'none';
      
      // Save to localStorage
      saveToLocalStorage(STORAGE_KEYS.SHUFFLE_MODE, newMode);
      
      return newMode;
    });
  };

  // Toggle mute
  const toggleMute = () => {
    if (!audioRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioRef.current.volume = newMuted ? 0 : volume;
    
    // Save to localStorage
    saveToLocalStorage(STORAGE_KEYS.IS_MUTED, newMuted);
  };

  // Toggle volume control visibility
  const toggleVolumeControl = () => {
    setShowVolumeControl(prev => !prev);
  };

  // Select song (UI handler)
  const selectSong = useCallback(async (song: Song) => {
    try {
      // Check if we're already loading this song
      if (isLoading && currentSongId === song.id) {
        // console.log('Already loading this song, ignoring duplicate request');
        return;
      }
      
      setIsLoading(true);
      
      // Reset any previous errors
      setAudioError(null);
      setDetailedError(null);
      
      // Keep current song playing until new one is loaded
      // Don't reset time yet
      
      // console.log(`Loading song: ${song.name} (${song.id})`);
      
      // Get the stream URL - this function now tries HLS first, then falls back to direct streaming
      const streamUrl = await getSongStreamUrl(song.id);
      
      if (!streamUrl) {
        console.error(`Stream URL for ${song.name} could not be generated`);
        toast.error(`Unable to play "${song.name}" - could not generate stream URL`);
        
        // Try to play the next song instead after a short delay
        setTimeout(() => {
          playNextSong();
        }, 500);
        
        setIsLoading(false);
        return;
      }
      
      // Check if this is an HLS stream or direct file
      const isHlsStream = streamUrl.includes('.m3u8');
      // console.log(`Retrieved stream URL for ${song.name}: ${isHlsStream ? 'HLS' : 'Direct'} playback`);
      
      // Now that we have the new song URL, update the song info first
      setCurrentSongId(song.id);
      setCurrentSong({ ...song, hlsUrl: streamUrl, isHls: isHlsStream });
      setSongUrl(streamUrl);
      
      // Then reset the time and start playing from beginning
      setCurrentTime(0);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
      
      // console.log(`Setting up ${isHlsStream ? 'HLS' : 'direct'} playback for: ${song.name}`);
      
      // Add a small delay before setting up playback to avoid potential race conditions
      setTimeout(() => {
        if (isHlsStream) {
          // Setup HLS playback
          setupHlsPlayback(
            streamUrl, 
            audioRef,
            setHlsInstance,
            hlsInstance,
            true
          );
        } else {
          // For direct streaming, just set the source on the audio element
          if (hlsInstance) {
            // Destroy any existing HLS instance
            try {
              hlsInstance.destroy();
            } catch (err) {
              console.error('Error destroying previous HLS instance:', err);
            }
            setHlsInstance(null);
          }
          
          if (audioRef.current) {
            // console.log(`Setting up direct streaming for ${song.name} at URL: ${streamUrl}`);
            audioRef.current.src = streamUrl;
            audioRef.current.currentTime = 0;
            
            // Add event listener for when metadata is loaded
            const onceLoaded = () => {
              if (audioRef.current) {
                audioRef.current.play()
                  .catch(err => {
                    console.error('Error playing direct stream:', err);
                    toast.error('Error playing song');
                  });
              }
              audioRef.current?.removeEventListener('loadedmetadata', onceLoaded);
            };
            
            audioRef.current.addEventListener('loadedmetadata', onceLoaded, { once: true });
            audioRef.current.load();
          }
        }
        
        // Mark loading as complete after setup
        setIsLoading(false);
      }, 100);
    } catch (error) {
      console.error('Song selection error:', error);
      toast.error('Failed to play song');
      
      // Try next song on fatal errors
      setTimeout(() => {
        playNextSong();
      }, 500);
      
      setIsLoading(false);
    }
  }, [hlsInstance, currentSongId, playNextSong]);

  // Load albums from API
  const loadAlbums = async () => {
    try {
      const loadedAlbums = await fetchAlbums();
      setAlbums(loadedAlbums);
      
      // Only set current album if not already set
      if (!currentAlbum && loadedAlbums.length > 0) {
        // Find the first album with songs
        const albumWithSongs = loadedAlbums.find(album => album.songs && album.songs.length > 0);
        
        if (albumWithSongs) {
          setCurrentAlbum(albumWithSongs);
          localStorage.setItem('currentAlbumId', albumWithSongs.id);
          
          // Try to load saved song or use first song *sorted by track number*
          const savedSongId = loadFromLocalStorage(STORAGE_KEYS.CURRENT_SONG, null);
          let songToPlay = null;
          
          if (savedSongId) {
            // Look for saved song in all albums
            for (const album of loadedAlbums) {
              const foundSong = album.songs.find((s: Song) => s.id === savedSongId);
              if (foundSong) {
                songToPlay = foundSong;
                break;
              }
            }
          }
          
          // If no saved song found, use the first song from the sorted list
          if (!songToPlay && albumWithSongs.songs.length > 0) {
            // Sort the songs by track number first
            const sortedSongs = [...albumWithSongs.songs].sort((a, b) => {
              if (a.trackNumber !== undefined && b.trackNumber !== undefined) {
                return a.trackNumber - b.trackNumber;
              }
              if (a.trackNumber !== undefined) return -1;
              if (b.trackNumber !== undefined) return 1;
              return 0;
            });
            songToPlay = sortedSongs[0]; // Select the first song from the sorted list

            // Only reset current time when starting with a new song (not the saved one)
            setCurrentTime(0);
            saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
          }
          
          // Load the song (but don't autoplay)
          if (songToPlay) {
            setTimeout(() => {
              playSong(songToPlay, false);
            }, 100);
          }
        } else if (loadedAlbums.length > 0) {
          setCurrentAlbum(loadedAlbums[0]);
          localStorage.setItem('currentAlbumId', loadedAlbums[0].id);
        }
      }
    } catch (error) {
      toast.error('Failed to load albums');
    }

    // Set loading to false after albums are fetched
    setLoading(false);
  };

  // Helper to get the currently active progress bar ref
  const getActiveProgressRef = (): React.RefObject<HTMLDivElement> | null => {
    // Check desktop ref first
    const desktopRect = desktopProgressRef.current?.getBoundingClientRect();
    if (desktopProgressRef.current && desktopRect && desktopRect.width > 0 && desktopProgressRef.current.offsetParent !== null) {
      // Check offsetParent to ensure it's actually visible
      return desktopProgressRef;
    }
    
    // Check mobile ref
    const mobileRect = mobileProgressRef.current?.getBoundingClientRect();
     if (mobileProgressRef.current && mobileRect && mobileRect.width > 0 && mobileProgressRef.current.offsetParent !== null) {
       return mobileProgressRef;
    }

    // Fallback if neither is found or visible (e.g., during initial render or layout shift)
    console.warn("Could not determine active progress bar ref. Attempting default.");
     // Prefer mobile first as it's often the default, then desktop
     if (mobileProgressRef.current) return mobileProgressRef;
     if (desktopProgressRef.current) return desktopProgressRef;

    return null; 
  };

  // UI event handlers
  // Progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const activeProgressRef = getActiveProgressRef();
    
    if (!activeProgressRef?.current || !audioRef.current) {
       console.error("Progress bar click: No active progress ref or audio element found.");
       return;
     }
    
    // Don't process if we were dragging
    if (isDraggingProgress) return;
    
    // Get the duration from the audio element directly
    const audioDuration = audioRef.current.duration;
    
    // Log detailed debugging info
    // console.log(`Progress bar clicked. Duration: ${audioDuration}, isDraggingProgress: ${isDraggingProgress}`);
    
    // Don't proceed if duration is invalid
    if (!audioDuration || !isFinite(audioDuration) || audioDuration <= 0) {
      console.error(`Invalid duration: ${audioDuration}`);
      return;
    }
    
    const rect = activeProgressRef.current.getBoundingClientRect();
    // Ensure we have a valid width to avoid division by zero
    if (!rect.width) {
      console.error("Progress bar has zero width on click");
      return;
    }
    
    const clickX = e.clientX;
    const rectLeft = rect.left;
    const rectWidth = rect.width;
    
    // Log the raw values to debug NaN issues
    // console.log(`Click values: clientX=${clickX}, rectLeft=${rectLeft}, rectWidth=${rectWidth}`);
    
    const x = Math.max(0, Math.min(rectWidth, clickX - rectLeft));
    const percentage = (x / rectWidth) * 100;
    
    // Verify the percentage calculation
    // console.log(`Calculated x=${x}, percentage=${percentage}%`);
    
    // Make sure percentage is valid before proceeding
    if (!isFinite(percentage) || isNaN(percentage)) {
      console.error("Invalid percentage calculated:", percentage);
      return;
    }
    
    // Calculate the new time but check if it's valid
    const rawTime = (percentage / 100) * audioDuration;
    // Make sure we don't go to the very end to prevent auto-advancing
    // Also ensure the time is finite and valid
    const time = Math.min(
      isFinite(rawTime) ? rawTime : 0, 
      isFinite(audioDuration) ? audioDuration - 0.1 : 0
    );
    
    if (!isFinite(time) || time < 0) {
      console.error("Invalid time value:", time);
      return;
    }
    
    // console.log(`Setting time to ${time} seconds (${percentage}% of ${audioDuration}s)`);
    
    // Update progress visually right away
    setProgress(percentage);
    setCurrentTime(time);
    
    // Jump to the new time immediately - wrap in try/catch to handle errors
    try {
      audioRef.current.currentTime = time;
      
      // Save time to localStorage
      saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, time);
    } catch (err) {
      console.error('Error setting audio current time:', err);
    }
    
    // Prevent event from bubbling up
    e.stopPropagation();
  };

  // Progress bar mouse down
  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const activeProgressRef = getActiveProgressRef();

    if (!activeProgressRef?.current || !audioRef.current) {
      console.error("Progress bar mouse down: No active progress ref or audio element found.");
      return;
    }
    
    // Get the duration from the audio element directly
    const audioDuration = audioRef.current.duration;
    
    // console.log(`Progress bar mouse down. Duration: ${audioDuration}`);
    
    // Don't proceed if duration is invalid
    if (!audioDuration || !isFinite(audioDuration) || audioDuration <= 0) {
      console.error(`Invalid duration for drag start: ${audioDuration}`);
      return;
    }
    
    // Set dragging state before calculating position
    setIsDraggingProgress(true);
    draggingRef.current = activeProgressRef; // Store the ref being dragged
    
    // Handle the initial click position
    const rect = activeProgressRef.current.getBoundingClientRect();
    // Ensure we have a valid width to avoid division by zero
    if (!rect.width) {
      console.error("Progress bar has zero width for drag start");
      setIsDraggingProgress(false);
      draggingRef.current = null; // Clear dragging ref
      return;
    }
    
    const clickX = e.clientX;
    const rectLeft = rect.left;
    const rectWidth = rect.width;
    
    // Log the raw values to debug NaN issues
    // console.log(`Drag start values: clientX=${clickX}, rectLeft=${rectLeft}, rectWidth=${rectWidth}`);
    
    const x = Math.max(0, Math.min(rectWidth, clickX - rectLeft));
    const percentage = (x / rectWidth) * 100;
    
    // Verify the percentage calculation
    // console.log(`Calculated drag x=${x}, percentage=${percentage}%`);
    
    // Make sure percentage is valid before proceeding
    if (!isFinite(percentage) || isNaN(percentage)) {
      console.error("Invalid percentage calculated for drag:", percentage);
      setIsDraggingProgress(false);
      draggingRef.current = null; // Clear dragging ref
      return;
    }
    
    // Calculate the new time but check if it's valid
    const rawTime = (percentage / 100) * audioDuration;
    // Make sure we don't go to the very end to prevent auto-advancing
    // Also ensure the time is finite and valid
    const time = Math.min(
      isFinite(rawTime) ? rawTime : 0, 
      isFinite(audioDuration) ? audioDuration - 0.1 : 0
    );
    
    if (!isFinite(time) || time < 0) {
      console.error("Invalid time value for drag:", time);
      setIsDraggingProgress(false);
      draggingRef.current = null; // Clear dragging ref
      return;
    }
    
    // console.log(`Drag start - setting time to ${time} seconds (${percentage}%)`);
    
    // Update progress visually only, don't set currentTime yet
    // This prevents seeking multiple times during drag
    setProgress(percentage);
    setCurrentTime(time);
    
    // Prevent event from bubbling up
    e.stopPropagation();
  };

  // Handle progress bar dragging
  useEffect(() => {
    // Only add listeners if dragging is active
    if (!isDraggingProgress) {
       draggingRef.current = null; // Clear ref when not dragging
       return;
    }
    
    // console.log('Progress drag effect activated');
    
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      const currentDraggingRef = draggingRef.current; // Use the stored ref
      if (!currentDraggingRef?.current || !audioRef.current) {
         console.warn("Drag move: No dragging ref or audio element.");
         return; // Exit if the ref isn't set or valid
      }
      
      // Get the duration from the audio element directly
      const audioDuration = audioRef.current.duration;
      
      // Don't proceed if duration is invalid
      if (!audioDuration || !isFinite(audioDuration) || audioDuration <= 0) {
        console.error(`Invalid duration during drag: ${audioDuration}`);
        return;
      }
      
      // Prevent text selection during drag
      e.preventDefault();
      
      const rect = currentDraggingRef.current.getBoundingClientRect();
      // Ensure we have a valid width to avoid division by zero
      if (!rect.width) {
        console.error("Progress bar has zero width during drag move");
        return;
      }
      
      // Get clientX for both mouse and touch events
      const clientX = 'touches' in e && e.touches.length > 0
        ? e.touches[0].clientX
        : ('clientX' in e ? e.clientX : 0);
      
      const rectLeft = rect.left;
      const rectWidth = rect.width;
      
      // Log the raw values to debug NaN issues during drag
      // console.log(`Drag move values: clientX=${clientX}, rectLeft=${rectLeft}, rectWidth=${rectWidth}`);
      
      const x = Math.max(0, Math.min(rectWidth, clientX - rectLeft));
      const percentage = (x / rectWidth) * 100;
      
      // Verify the percentage calculation during drag
      // console.log(`Calculated drag move x=${x}, percentage=${percentage}%`);
      
      // Make sure percentage is valid before proceeding with drag move
      if (!isFinite(percentage) || isNaN(percentage)) {
        console.error("Invalid percentage calculated during drag move:", percentage);
        return;
      }
      
      // Calculate the new time but check if it's valid
      const rawTime = (percentage / 100) * audioDuration;
      // Make sure we don't go to the very end to prevent auto-advancing
      // Also ensure the time is finite and valid
      const time = Math.min(
        isFinite(rawTime) ? rawTime : 0, 
        isFinite(audioDuration) ? audioDuration - 0.1 : 0
      );
      
      if (!isFinite(time) || time < 0) {
        console.error(`Invalid time during drag move: ${time}`);
        return;
      }
      
      // Update progress visually only during drag
      setProgress(percentage);
      setCurrentTime(time);
    };
    
    const handleMouseUp = (e: MouseEvent | TouchEvent) => {
       const currentDraggingRef = draggingRef.current; // Use the stored ref

      if (!currentDraggingRef?.current || !audioRef.current) {
         console.warn("Drag end (mouse up): No dragging ref or audio element.");
         setIsDraggingProgress(false); // Ensure dragging state is reset
         draggingRef.current = null;   // Clear ref
         return; // Exit if the ref isn't set or valid
      }
      
      // Get the duration from the audio element directly
      const audioDuration = audioRef.current.duration;
      
      // console.log(`Mouse up after dragging. Duration: ${audioDuration}`);
      
      // Don't proceed if duration is invalid
      if (!audioDuration || !isFinite(audioDuration) || audioDuration <= 0) {
        console.error(`Invalid duration on mouse up: ${audioDuration}`);
        setIsDraggingProgress(false);
        draggingRef.current = null; // Clear ref
        return;
      }
      
      // Calculate final position
      const rect = currentDraggingRef.current.getBoundingClientRect();
      // Ensure we have a valid width on mouse up
      if (!rect.width) {
        console.error("Progress bar has zero width on mouse up");
        setIsDraggingProgress(false);
        draggingRef.current = null; // Clear ref
        return;
      }
      
      // Get clientX for both mouse and touch events
      const clientX = 'changedTouches' in e && e.changedTouches.length > 0
        ? e.changedTouches[0].clientX
        : ('clientX' in e ? e.clientX : 0);
      
      const rectLeft = rect.left;
      const rectWidth = rect.width;
      
      // Log the raw values to debug NaN issues on mouse up
      // console.log(`Mouse up values: clientX=${clientX}, rectLeft=${rectLeft}, rectWidth=${rectWidth}`);
      
      const x = Math.max(0, Math.min(rectWidth, clientX - rectLeft));
      const percentage = (x / rectWidth) * 100;
      
      // Verify the percentage calculation on mouse up
      // console.log(`Calculated mouse up x=${x}, percentage=${percentage}%`);
      
      // Make sure percentage is valid before proceeding with mouse up
      if (!isFinite(percentage) || isNaN(percentage)) {
        console.error("Invalid percentage calculated on mouse up:", percentage);
        setIsDraggingProgress(false);
        draggingRef.current = null; // Clear ref
        return;
      }
      
      // Calculate the new time but check if it's valid
      const rawTime = (percentage / 100) * audioDuration;
      // Make sure we don't go to the very end to prevent auto-advancing
      // Also ensure the time is finite and valid
      const time = Math.min(
        isFinite(rawTime) ? rawTime : 0, 
        isFinite(audioDuration) ? audioDuration - 0.1 : 0
      );
      
      if (!isFinite(time) || time < 0) {
        console.error(`Invalid time on mouse up: ${time}`);
        setIsDraggingProgress(false);
        draggingRef.current = null; // Clear ref
        return;
      }
      
      // console.log(`Drag end - setting final time to ${time} seconds (${percentage}%)`);
      
      // Update the actual audio time safely
      try {
        audioRef.current.currentTime = time;
        
        // Save time to localStorage
        saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, time);
      } catch (err) {
        console.error('Error setting audio current time after drag:', err);
      }
      
      // End dragging
      setIsDraggingProgress(false);
      draggingRef.current = null; // Clear ref
    };
    
    // Add event listeners for both mouse and touch events
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove as EventListener, { passive: false });
    document.addEventListener('touchend', handleMouseUp as EventListener);
    
    // Clean up
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove as EventListener);
      document.removeEventListener('touchend', handleMouseUp as EventListener);
    };
  }, [isDraggingProgress, audioRef]);

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setVolume(value);
    if (audioRef.current) {
      audioRef.current.volume = value;
    }
    setIsMuted(value === 0);
    
    // Save to localStorage
    saveToLocalStorage(STORAGE_KEYS.VOLUME, value);
  };

  // Handle click outside menus
  useEffect(() => {
    // Function to handle clicks outside menus
    const handleClickOutside = (event: MouseEvent) => {
      // Handle volume control click outside
      if (showVolumeControl) {
        // Check if clicked on the volume button itself
        const volumeButtons = document.querySelectorAll('.volume-toggle-btn');
        const isClickOnVolumeButton = Array.from(volumeButtons).some(button => 
          button.contains(event.target as Node)
        );
        
        // If clicked on the volume button, let the toggle handler manage the state
        if (isClickOnVolumeButton) {
          return;
        }
        
        // Check if clicked inside the volume control
        const volumeControls = document.querySelectorAll('.volume-control-popup');
        const isClickInsideVolumeControl = Array.from(volumeControls).some(control => 
          control.contains(event.target as Node)
        );
        
        // Only close if clicked outside the volume control and not on the volume button
        if (!isClickInsideVolumeControl) {
          setShowVolumeControl(false);
        }
      }
      
      // Get references to all album buttons (desktop and mobile)
      const albumButtons = document.querySelectorAll('button[title="Select Album"]');
      const isClickOnAlbumButton = Array.from(albumButtons).some(button => 
        button.contains(event.target as Node)
      );
      
      // Handle album selection menu click outside
      if (showAlbums) {
        // Don't close if clicking on any album button
        if (isClickOnAlbumButton || (albumButtonRef.current && albumButtonRef.current.contains(event.target as Node))) {
          return;
        }
        
        // Close if clicking outside of menu
        if (albumMenuRef.current && !albumMenuRef.current.contains(event.target as Node)) {
          setShowAlbums(false);
        }
      }
      
      // Handle playlist menu click outside
      if (showPlaylist) {
        // Don't close if clicking on the button that opened it
        const playlistButtons = document.querySelectorAll('button[title="Show Playlist"]');
        const isClickOnPlaylistButton = Array.from(playlistButtons).some(button => 
          button.contains(event.target as Node)
        ) || (playlistButtonRef.current && playlistButtonRef.current.contains(event.target as Node));
        
        if (isClickOnPlaylistButton) {
          return;
        }
        
        // Close if clicking outside of menu
        if (playlistMenuRef.current && !playlistMenuRef.current.contains(event.target as Node)) {
          setShowPlaylist(false);
        }
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVolumeControl, showAlbums, showPlaylist]);

  // Check playlist scroll indicators when playlist opens or songs change
  useEffect(() => {
    if (showPlaylist && playlistScrollRef.current) {
      const container = playlistScrollRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isScrollable = scrollHeight > clientHeight;

      setShowPlaylistTopArrow(scrollTop > 10);
      setShowPlaylistBottomArrow(isScrollable && scrollTop < scrollHeight - clientHeight - 10);
    }
  }, [showPlaylist, currentAlbum?.songs]);

  // Keyboard controls
  // Global keyboard controls (seek ±5s, volume ±10%, space toggles play)
  useKeyboardShortcuts({
    audioRef,
    duration,
    volume,
    setCurrentTime,
    setVolume,
    setIsMuted,
    onTogglePlay: handlePlayPause,
  });

  // Setup audio event listeners
  useEffect(() => {
    if (!audioRef.current) return;
    
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      // Don't update progress if user is dragging
      if (isDraggingProgress) return;
      
      const currTime = audio.currentTime;
      
      // Set current time in component state
      setCurrentTime(currTime);
      
      // Calculate progress percentage
      setProgress((currTime / (audio.duration || 1)) * 100);
    };
    
    const handleAudioPlay = () => {
      setIsPlaying(true);

      // Initialize visualizer if not already done
      if (!visualizerInitializedRef.current && audioRef.current) {
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass && !audioContext) {
            const newAudioContext = new AudioContextClass();
            const newAnalyser = newAudioContext.createAnalyser();
            newAnalyser.fftSize = 2048;
            newAnalyser.smoothingTimeConstant = 0.8;

            const newSource = newAudioContext.createMediaElementSource(audioRef.current);
            newSource.connect(newAnalyser);
            newAnalyser.connect(newAudioContext.destination);

            setAudioContext(newAudioContext);
            setSource(newSource);
            setAnalyser(newAnalyser);
            visualizerInitializedRef.current = true;

            // Resume and notify
            newAudioContext.resume().then(() => {
              if (onVisualizerUpdate) {
                onVisualizerUpdate(newAnalyser, true);
              }
            });
          }
        } catch (error) {
          console.error('Failed to initialize visualizer on play event:', error);
        }
      }
      // If already initialized, just update
      else if (visualizerInitializedRef.current && audioContext && analyser && onVisualizerUpdate) {
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            onVisualizerUpdate(analyser, true);
          }).catch(err => {
            console.error('Failed to resume AudioContext:', err);
          });
        } else {
          onVisualizerUpdate(analyser, true);
        }
      }
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      // Update visualizer if available
      if (analyser && onVisualizerUpdate) {
        onVisualizerUpdate(analyser, false);
      }
      
      // Save time when paused
      if (currentSong && audioRef.current) {
        saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, audioRef.current.currentTime);
      }
    };
    
    const handleEnded = () => {
      handleSongEnd();
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      
      // Only restore saved time if it's for the current song
      // and the song isn't changing due to playNextSong
      const savedTime = loadFromLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
      const lastPlayedSong = loadFromLocalStorage(STORAGE_KEYS.CURRENT_SONG, null);
      
      // Log saved values to verify what's being loaded
      // console.log('Metadata loaded, saved values:', { 
      //   savedTime, 
      //   lastPlayedSong, 
      //   currentSongId: currentSong?.id,
      //   audioDuration: audio.duration 
      // });
      
      // Check if we're loading the same song as before
      if (currentSong && lastPlayedSong === currentSong.id && savedTime > 0 && 
          audio.duration && isFinite(audio.duration) && savedTime < audio.duration) {
        // Restore the saved time position
        audio.currentTime = savedTime;
        setCurrentTime(savedTime);
        // console.log(`Restored saved position: ${savedTime} seconds`);
      } else {
        // Reset saved time for new songs
        // console.log('Not restoring saved time because:', {
        //   hasCurrSong: !!currentSong,
        //   songMatch: lastPlayedSong === currentSong?.id,
        //   validSavedTime: savedTime > 0,
        //   hasDuration: !!audio.duration,
        //   durationIsFinite: isFinite(audio.duration),
        //   timeIsValid: savedTime < (audio.duration || Infinity)
        // });
        saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
        setCurrentTime(0);
        audio.currentTime = 0;
      }
    };
    
    // Set volume immediately
    audio.volume = isMuted ? 0 : volume;
    
    // Add event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handleAudioPlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    // Clean up
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handleAudioPlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audioRef.current, isDraggingProgress, volume, isMuted, analyser, onVisualizerUpdate, handleSongEnd, currentSong, audioContext, source]);

  // Setup volume change listener for mobile volume control
  useEffect(() => {
    const handleVolumeChange = (e: any) => {
      if (e.detail && typeof e.detail.volume === 'number') {
        const value = e.detail.volume;
        setVolume(value);
        if (audioRef.current) {
          audioRef.current.volume = value;
        }
        setIsMuted(value === 0);
        
        // Save to localStorage
        saveToLocalStorage(STORAGE_KEYS.VOLUME, value);
      }
    };

    window.addEventListener('volumeChange', handleVolumeChange);
    
    return () => {
      window.removeEventListener('volumeChange', handleVolumeChange);
    };
  }, []);

  // Initialize audio visualizer - replace the entire useEffect
  useEffect(() => {
    // Use ref to track initialization state across renders
    if (visualizerInitializedRef.current || !audioRef.current) return;

    const initializeAudioVisualization = async () => {
      // Check if already initialized using ref
      if (visualizerInitializedRef.current || !audioRef.current) return;

      try {
        // console.log('Initializing audio visualization...');

        // Create AudioContext
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          console.error('AudioContext not supported in this browser');
          return;
        }

        const newAudioContext = new AudioContextClass();

        // Create analyzer
        const newAnalyser = newAudioContext.createAnalyser();
        newAnalyser.fftSize = 2048;
        newAnalyser.smoothingTimeConstant = 0.8;

        // Connect to audio element
        if (audioRef.current) {
          const newSource = newAudioContext.createMediaElementSource(audioRef.current);
          newSource.connect(newAnalyser);
          newAnalyser.connect(newAudioContext.destination);

          // Update state
          setAudioContext(newAudioContext);
          setSource(newSource);
          setAnalyser(newAnalyser);

          // Mark as initialized using ref to prevent multiple initializations
          visualizerInitializedRef.current = true;
          // console.log('Audio visualization initialized successfully');

          // If audio is already playing, notify parent immediately
          if (audioRef.current && !audioRef.current.paused && onVisualizerUpdate) {
            newAudioContext.resume().then(() => {
              onVisualizerUpdate(newAnalyser, true);
            });
          }
        }
      } catch (error) {
        console.error('Failed to initialize audio visualization:', error);
      }
    };

    // We'll only initialize on play event, not automatically
    const handlePlayForInit = () => {
      if (!visualizerInitializedRef.current) {
        // console.log('Play event detected, initializing visualizer');
        initializeAudioVisualization();
      }
    };

    // Add event listener for play to initialize
    if (audioRef.current) {
      audioRef.current.addEventListener('play', handlePlayForInit);
    }

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('play', handlePlayForInit);
      }

      if (source) {
        try {
          source.disconnect();
        } catch (err) {
          console.error('Error disconnecting source during cleanup:', err);
        }
      }
    };
  }, []); // Run only once on mount, use refs for checking state

  // Update tooltip visibility
  useEffect(() => {
    // Show tooltip again after 5 seconds of inactivity
    const showTooltipTimer = setTimeout(() => {
      if (!showAlbums && !showPlaylist) {
        setShowTooltip(true);
      }
    }, 1500);
    
    return () => clearTimeout(showTooltipTimer);
  }, [showAlbums, showPlaylist, currentSong]);

  // Save current song ID to localStorage
  useEffect(() => {
    if (currentSong) {
      saveToLocalStorage(STORAGE_KEYS.CURRENT_SONG, currentSong.id);
    }
  }, [currentSong]);

  // Save current time to localStorage while playing
  useEffect(() => {
    if (!isDraggingProgress && currentTime > 0 && currentSong) {
      saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, currentTime);
    }
  }, [currentTime, isDraggingProgress, currentSong]);

  // Clean up HLS instance on unmount
  useEffect(() => {
    return () => {
      if (hlsInstance) {
        try {
          hlsInstance.destroy();
        } catch (err) {
          console.error('Error destroying HLS instance:', err);
        }
      }
    };
  }, [hlsInstance]);

  // Clean up HLS instance on song change
  useEffect(() => {
    if (hlsInstance && currentSong?.id) {
      return () => {
        try {
          hlsInstance.destroy();
          setHlsInstance(null);
        } catch (err) {
          console.error('Error destroying HLS instance on song change:', err);
        }
      };
    }
  }, [currentSong?.id]);

  // Add listener for song stream errors
  useEffect(() => {
    const handleSongStreamError = (e: any) => {
      // console.log('Song stream error detected, trying next song:', e.detail);
      // Wait a short delay then play next song
      setTimeout(() => {
        playNextSong();
      }, 500);
    };

    document.addEventListener('songStreamError', handleSongStreamError);
    
    return () => {
      document.removeEventListener('songStreamError', handleSongStreamError);
    };
  }, []);

  // Play a song with HLS or direct streaming
  const playSong = useCallback(async (song: Song, autoPlay = true) => {
    if (!song) return;
    
    try {
      setIsLoading(true);
      setCurrentSong(song);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_SONG, song.id); // Save just the ID
      setCurrentSongId(song.id);
      
      // Get stream URL with fallback
      const streamUrl = await getSongStreamUrl(song.id);
      
      if (!streamUrl) {
        console.error(`Stream URL for ${song.name} could not be generated`);
        toast.error(`Unable to play "${song.name}" - could not generate stream URL`);
        setIsLoading(false);
        return;
      }
      
      // Check if this is an HLS stream or direct file
      const isHlsStream = streamUrl.includes('.m3u8');
      
      // Update song info
      setCurrentSong({ ...song, hlsUrl: streamUrl, isHls: isHlsStream });
      setSongUrl(streamUrl);
      
      // Get saved time for this song (will be restored in handleLoadedMetadata)
      const savedTime = loadFromLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
      const lastPlayedSong = loadFromLocalStorage(STORAGE_KEYS.CURRENT_SONG, null);
      const shouldRestoreTime = lastPlayedSong === song.id && savedTime > 0;
      
      // Only reset time if it's not the last played song or we're explicitly changing songs
      if (!shouldRestoreTime) {
        setCurrentTime(0);
        saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, 0);
      }
      
      // Setup appropriate playback method
      if (isHlsStream) {
        // Setup HLS playback
        setupHlsPlayback(
          streamUrl, 
          audioRef,
          setHlsInstance,
          hlsInstance,
          autoPlay
        );
      } else {
        // For direct streaming, just set the source on the audio element
        if (hlsInstance) {
          // Destroy any existing HLS instance
          try {
            hlsInstance.destroy();
          } catch (err) {
            console.error('Error destroying previous HLS instance:', err);
          }
          setHlsInstance(null);
        }
        
        if (audioRef.current) {
          audioRef.current.src = streamUrl;
          
          // Don't reset currentTime here - we'll let handleLoadedMetadata manage this
          // based on saved state
          
          if (autoPlay) {
            // Add event listener for when metadata is loaded
            const onceLoaded = () => {
              if (audioRef.current) {
                audioRef.current.play()
                  .catch(err => {
                    console.error('Error playing direct stream:', err);
                    toast.error('Error playing song');
                  });
              }
              audioRef.current?.removeEventListener('loadedmetadata', onceLoaded);
            };
            
            audioRef.current.addEventListener('loadedmetadata', onceLoaded, { once: true });
          }
          
          audioRef.current.load();
        }
      }
    } catch (error) {
      console.error('Error in playSong:', error);
      toast.error('Failed to play song');
    } finally {
      setIsLoading(false);
    }
  }, [hlsInstance]);

  // --- Media Session API Integration ---

  // Function to get proper image URL for album covers
  // getProperImageUrl moved to ./utils (shared, deduplicated)

  // Update Media Session metadata when song or artwork changes
  useEffect(() => {
    if ('mediaSession' in navigator && currentSong) {
      try {
        // Make sure the coverArt URL is properly formatted
        const artworkUrl = coverArt || '';
        
        // Ensure we're not combining URLs incorrectly
        let cleanArtworkUrl = artworkUrl;
        
        // Handle various malformed URL patterns
        if (artworkUrl.startsWith('blob:')) {
          // If it's a blob URL, use it directly
          cleanArtworkUrl = artworkUrl;
        } 
        // Handle case where http://localhost:3001 combines with blob:
        else if (artworkUrl.includes('localhost:3001') && artworkUrl.includes('blob:')) {
          cleanArtworkUrl = artworkUrl.substring(artworkUrl.indexOf('blob:'));
        }
        // Handle case where http://localhost3001 (missing colon) combines with blob:
        else if (artworkUrl.includes('localhost3001') && artworkUrl.includes('blob:')) {
          cleanArtworkUrl = artworkUrl.substring(artworkUrl.indexOf('blob:'));
        }
        // Handle any other case where there's a blob:
        else if (artworkUrl.includes('blob:')) {
          cleanArtworkUrl = artworkUrl.substring(artworkUrl.indexOf('blob:'));
        }
        
        // console.log(`Setting MediaMetadata artwork URL: ${cleanArtworkUrl}`);
        
        // Create metadata object with basic info first
        const metadata: MediaMetadataInit = {
          title: currentSong.name,
          artist: currentAlbum?.name || '', 
          album: currentAlbum?.name || ''
        };
        
        // Only add artwork if URL is valid
        if (cleanArtworkUrl && cleanArtworkUrl.trim() !== '') {
          metadata.artwork = [
            { src: cleanArtworkUrl, sizes: '512x512', type: 'image/jpeg' }
          ];
        }
        
        // Set the metadata
        navigator.mediaSession.metadata = new MediaMetadata(metadata);
      } catch (error) {
        console.error('Error setting MediaMetadata:', error);
        // Fallback to basic metadata without artwork on error
        try {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: currentSong.name,
            artist: currentAlbum?.name || '',
            album: currentAlbum?.name || ''
          });
        } catch (innerError) {
          console.error('Fatal error setting MediaMetadata:', innerError);
        }
      }
    }
  }, [currentSong, currentAlbum, coverArt]);

  // Set Media Session action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    // Use existing callbacks directly
    const actionHandlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ['play', handlePlayPause],
      ['pause', handlePlayPause],
      ['previoustrack', playPreviousSong],
      ['nexttrack', playNextSong],
      // Add more handlers like seekbackward, seekforward if needed
    ];

    for (const [action, handler] of actionHandlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch (error) {
        console.warn(`The media session action '${action}' is not supported.`);
      }
    }

    // Cleanup: Remove handlers when component unmounts or handlers change
    return () => {
      for (const [action] of actionHandlers) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch (error) {
           // Ignore errors during cleanup
        }
      }
    };
  // Depend on the handler functions to reset if they change instance
  }, [handlePlayPause, playPreviousSong, playNextSong]); 

  // Update Media Session playback state
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // --- End Media Session API Integration ---

  // Skip rendering if not loaded or no song available
  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-white">Loading music...</div>
      </div>
    );
  }

  if (!currentSong) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-white">No songs available</div>
      </div>
    );
  }

  // Add handleDownload inside the component
  const handleDownload = () => downloadPublicMedia(currentSong?.songUrl);

  // Seek helper (used when clicking a synced lyric line)
  const seekToTime = (t: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = t;
      setCurrentTime(t);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_TIME, t);
    }
  };

  return (
    <div className="relative">
      {/* Synced lyrics (Tidal/Spotify style) — portal to body so its fixed
          positioning escapes the player bar's backdrop-filter containing block */}
      {createPortal(
        <SyncedLyrics
          currentSongId={currentSong?.id || null}
          currentTime={currentTime}
          isPlaying={isPlaying}
          onSeek={seekToTime}
        />,
        document.body
      )}
      {/* Create portal for mobile song info at the top of the page */}
      {mobileInfoPortalElement && currentSong && createPortal(
        <MobileSongInfo
          currentSong={currentSong}
          currentAlbum={currentAlbum}
          coverArt={coverArt}
          isPlaying={isPlaying}
          albumButtonRef={albumButtonRef}
          setShowAlbums={setShowAlbums}
          showAlbums={showAlbums}
          setShowPlaylist={setShowPlaylist}
          setShowTooltip={setShowTooltip}
        />,
        mobileInfoPortalElement
      )}


      {/* Native audio element */}
      <audio
        ref={audioRef}
        preload="none"
        crossOrigin="anonymous"
        className="hidden"
        controlsList="nodownload"
        style={{ display: 'none' }}
        onError={(e) => {
          // Only report errors after initial setup
          if (hlsInstance) {
            if (audioRef.current?.error) {
              const errorCode = audioRef.current.error.code;
              const errorMessage = `Audio error (${errorCode}): ${audioRef.current.error.message || 'Unknown error'}`;
              setAudioError(errorMessage);
              
              // Add additional browser-specific debugging info
              setDetailedError(`Browser info: ${browserInfo || navigator.userAgent}`);
            }
          }
        }}
      />

      {/* Error display */}
      <AudioErrorDisplay 
        audioError={audioError}
        detailedError={detailedError}
        songUrl={songUrl}
        currentSong={currentSong}
        setupNativeHlsPlayback={(url, autoPlay) => 
          setupNativeHlsPlayback(
            url, 
            audioRef, 
            setAudioError, 
            setDetailedError, 
            hlsInstance, 
            setHlsInstance, 
            autoPlay
          )
        }
        setupHlsPlayback={(url, autoPlay) => 
          setupHlsPlayback(
            url, 
            audioRef, 
            setHlsInstance, 
            hlsInstance, 
            autoPlay
          )
        }
        browserInfo={browserInfo}
      />


      {/* Desktop Layout (hidden on small screens) */}
      <div className="flex-col items-center hidden sm:flex">
        {/* Main controls */}
        <DesktopControls 
          isPlaying={isPlaying}
          isLoading={isLoading}
          repeatMode={repeatMode}
          shuffleMode={shuffleMode}
          isMuted={isMuted}
          handlePlayPause={handlePlayPause}
          playPreviousSong={playPreviousSong}
          playNextSong={playNextSong}
          toggleRepeatMode={toggleRepeatMode}
          toggleShuffleMode={toggleShuffleMode}
          toggleMute={toggleMute}
          toggleVolumeControl={toggleVolumeControl}
          currentSong={currentSong}
        />

        <div className="flex items-center justify-between w-full max-w-[55rem] px-4 mb-[9px]">
          {/* Album info */}
          <AlbumDisplay 
            currentAlbum={currentAlbum}
            currentSong={currentSong}
            coverArt={coverArt}
            isPlaying={isPlaying}
            albumButtonRef={albumButtonRef}
            setShowAlbums={setShowAlbums}
            showAlbums={showAlbums}
            setShowPlaylist={setShowPlaylist}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
          />

          {/* Right side controls */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">

            {currentSong?.downloadable && (
          <button
            onClick={handleDownload}
            className="text-gray-400 hover:text-white transition-colors"
            title="Download song"
          >
            <Download size={24} />
          </button>
        )}
              
                <button 
                  onClick={toggleRepeatMode}
                  className="transition-colors tooltip-container"
                  title={
                    repeatMode === 'none' 
                      ? 'Repeat: Off' 
                      : repeatMode === 'album' 
                        ? 'Repeat: Album' 
                        : 'Repeat: Song'
                  }
                  data-repeat-mode={repeatMode}
                >
                  {repeatMode === 'none' ? (
                    <Repeat size={24} className="text-gray-500" />
                  ) : repeatMode === 'album' ? (
                    <Repeat size={24} className="text-white" />
                  ) : (
                    <Repeat1 size={24} className="text-white" />
                  )}
                </button>
              
              
              <button 
                onClick={toggleShuffleMode}
                className="transition-colors tooltip-container relative"
                title={
                  shuffleMode === 'none' 
                    ? 'Shuffle: Off' 
                    : shuffleMode === 'album' 
                      ? 'Shuffle: Album' 
                      : 'Shuffle: All Songs'
                }
              >
                {shuffleMode === 'none' ? (
                  <Shuffle size={24} className="text-gray-500" />
                ) : (
                  <Shuffle size={24} className="text-white" />
                )}
                {shuffleMode === 'album' && (
                  <span className="absolute -top-1 -right-1 text-[10px] bg-white text-black w-4 h-4 flex items-center justify-center">A</span>
                )}
                {shuffleMode === 'all' && (
                  <span className="absolute -top-1 -right-1 text-[10px] bg-white text-black w-4 h-4 flex items-center justify-center">*</span>
                )}
              </button>
              
              <button
                ref={playlistButtonRef}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  // console.log("Playlist button clicked");
                  setShowPlaylist(prev => !prev);
                  setShowAlbums(false);
                }}
                className="text-gray-400 hover:text-white transition-colors"
                title="Show Playlist"
              >
                <ListMusic size={24} />
              </button>
              
              <div className="relative flex items-center volume-control-container"
                onMouseEnter={() => {
                  // Clear any existing timeout when mouse enters
                  if (volumeHideTimeoutRef.current) {
                    clearTimeout(volumeHideTimeoutRef.current);
                    volumeHideTimeoutRef.current = null;
                  }
                  setShowVolumeControl(true);
                }}
                onMouseLeave={() => {
                  // Set a timeout before hiding the volume control
                  volumeHideTimeoutRef.current = setTimeout(() => {
                    setShowVolumeControl(false);
                  }, 1000); // 1 second delay
                }}
                onClick={() => {
                  // Add click handler for mobile
                  if (window.innerWidth <= 640) {
                    toggleVolumeControl();
                  }
                }}
              >
                <button 
                  onClick={toggleMute} 
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                </button>
                
                <VolumeControl 
                  showVolumeControl={showVolumeControl}
                  volume={volume}
                  setVolume={setVolume}
                  setIsMuted={setIsMuted}
                  audioRef={audioRef}
                  volumeHideTimeoutRef={volumeHideTimeoutRef}
                  setShowVolumeControl={setShowVolumeControl}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar for desktop layout */} 
      <div className="w-full max-w-[56rem] px-2 hidden sm:block">
        <div 
          ref={desktopProgressRef}
          className="relative h-1 bg-zinc-800 cursor-pointer progress-bar"
          onClick={handleProgressClick}
          onMouseDown={handleProgressMouseDown}
        >
          <div 
            className="absolute h-full bg-white"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white" />
          </div>
        </div>
        <div className="flex mb-0 justify-between mt-1 text-xs text-gray-400">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(audioRef.current?.duration || duration)}</span>
        </div>
      </div>

      {/* Mobile Layout (visible only on small screens) */}
      <div className="flex-col items-center sm:hidden mobile-player-layout">
        {/* Progress bar at the top */} 
        <ProgressBar 
          progress={progress}
          currentTime={currentTime}
          duration={duration}
          progressRef={mobileProgressRef}
          handleProgressClick={handleProgressClick}
          handleProgressMouseDown={handleProgressMouseDown}
          formatTime={formatTime}
          currentAudioTime={audioRef.current?.duration || 0}
        />

        {/* Mobile song info is in portal at the top */} 

        {/* Large playback controls */} 
        <MobileControls 
          isPlaying={isPlaying}
          isLoading={isLoading}
          repeatMode={repeatMode}
          shuffleMode={shuffleMode}
          isMuted={isMuted}
          handlePlayPause={handlePlayPause}
          playPreviousSong={playPreviousSong}
          playNextSong={playNextSong}
          toggleRepeatMode={toggleRepeatMode}
          toggleShuffleMode={toggleShuffleMode}
          toggleMute={toggleMute}
          toggleVolumeControl={toggleVolumeControl}
          currentSong={currentSong}
        />

        {/* Additional controls */} 
        <BottomControls 
          repeatMode={repeatMode}
          shuffleMode={shuffleMode}
          isMuted={isMuted}
          isPlaying={isPlaying}
          isLoading={isLoading}
          handlePlayPause={handlePlayPause}
          playPreviousSong={playPreviousSong}
          playNextSong={playNextSong}
          toggleRepeatMode={toggleRepeatMode}
          toggleShuffleMode={toggleShuffleMode}
          toggleMute={toggleMute}
          toggleVolumeControl={toggleVolumeControl}
          playlistButtonRef={playlistButtonRef}
          showPlaylist={showPlaylist}
          setShowPlaylist={setShowPlaylist}
          setShowAlbums={setShowAlbums}
          showVolumeControl={showVolumeControl}
          volume={volume}
          currentSong={currentSong}
        />
      </div>

      {/* Album selection menu */} 
      <AnimatePresence>
        {showAlbums && (
          <motion.div
            ref={albumMenuRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full left-0 right-0 mb-2 nothing-card z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg uppercase tracking-wider mb-4">Albums</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {albums.map((album) => {
                // Find the first song based on track number for this album
                let firstSongInAlbum = null;
                if (album.songs && album.songs.length > 0) {
                   const sortedSongs = [...album.songs].sort((a, b) => {
                    if (a.trackNumber !== undefined && b.trackNumber !== undefined) {
                      return a.trackNumber - b.trackNumber;
                    }
                    if (a.trackNumber !== undefined) return -1;
                    if (b.trackNumber !== undefined) return 1;
                    return 0;
                  });
                  firstSongInAlbum = sortedSongs[0];
                }
                
                return (
                  <motion.button
                    key={album.id}
                    onClick={() => {
                      // Play the first song *by track number* if available
                      if (firstSongInAlbum) {
                        selectSong(firstSongInAlbum);
                      } else {
                        console.warn(`Album ${album.name} has no songs or failed to sort.`);
                      }
                      setShowAlbums(false);
                    }}
                    className={`relative group ${currentAlbum?.id === album.id ? 'ring-2 ring-white' : ''}`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {album.coverUrl ? (
                      <img 
                        src={getProperImageUrl(album.coverUrl)} 
                        alt={album.name}
                        className="w-full aspect-square object-cover filter grayscale"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-800 flex items-center justify-center">
                        <Music className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                        <h3 className="text-sm sm:text-xl uppercase tracking-wider">{album.name}</h3>
                        <p className="text-gray-400 text-xs sm:text-base">{album.songs?.length || 0} tracks</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Playlist menu */}
      <AnimatePresence>
        {showPlaylist && currentAlbum && (
          <motion.div
            ref={playlistMenuRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full left-0 right-0 mb-2 nothing-card z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg uppercase tracking-wider mb-4">Current Album</h4>
            <div className="relative">
              {/* Top scroll indicator */}
              <AnimatePresence>
                {showPlaylistTopArrow && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-0 left-0 right-0 z-10 flex justify-center pointer-events-none py-2"
                  >
                    <ChevronUp className="text-red-500 animate-bounce" size={24} strokeWidth={3} />
                  </motion.div>
                )}
              </AnimatePresence>

              <div
                ref={playlistScrollRef}
                className="space-y-2 max-h-64 overflow-y-auto playlist-container"
                onScroll={() => {
                  const container = playlistScrollRef.current;
                  if (!container) return;

                  const { scrollTop, scrollHeight, clientHeight } = container;
                  const isScrollable = scrollHeight > clientHeight;

                  setShowPlaylistTopArrow(scrollTop > 10);
                  setShowPlaylistBottomArrow(isScrollable && scrollTop < scrollHeight - clientHeight - 10);
                }}
              >
              {currentAlbum.songs
                // Sort songs by track number if available, otherwise keep original order
                .slice()
                .sort((a, b) => {
                  // If both have track numbers, sort by track number
                  if (a.trackNumber !== undefined && b.trackNumber !== undefined) {
                    return a.trackNumber - b.trackNumber;
                  }
                  // If only one has track number, put the one with track number first
                  if (a.trackNumber !== undefined) return -1;
                  if (b.trackNumber !== undefined) return 1;
                  // Default: keep original order
                  return 0;
                })
                .map((song) => (
                <motion.button
                  key={song.id}
                  onClick={() => {
                    selectSong(song);
                    setShowPlaylist(false);
                  }}
                  className={`w-full text-left p-3 hover:bg-zinc-800 transition-colors ${
                    currentSong.id === song.id ? 'bg-zinc-800' : ''
                  }`}
                  whileHover={{ x: 4 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {/* Display track number as separate element before the song name */}
                      {song.trackNumber !== undefined && (
                        <span className="text-gray-400 mr-3 min-w-[20px] text-right" style={{ color: 'red' }}>
                          {song.trackNumber}.
                        </span>
                      )}
                      <div className="uppercase tracking-wider truncate max-w-[150px] sm:max-w-none text-sm sm:text-base">
                        {song.name}
                      </div>
                    </div>
                    <div className="text-gray-400 tracking-wider text-xs sm:text-sm">{song.duration}</div>
                  </div>
                </motion.button>
              ))}
              </div>

              {/* Bottom scroll indicator */}
              <AnimatePresence>
                {showPlaylistBottomArrow && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute bottom-0 left-0 right-0 z-10 flex justify-center pointer-events-none py-2"
                  >
                    <ChevronDown className="text-red-500 animate-bounce" size={24} strokeWidth={3} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MusicPlayer; 